/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Injectable } from '@angular/core';
import { pipe } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { LogsEffectsService } from '../../../services/logs-effects.service';
import { LogsSearchBarDropdownService } from '../../../services/logs-search-bar-dropdown.service';

import { ILogsFullGroupbyParams, TLogsPageStateParams } from '../../../logs.types';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { ILogsSearchBarStates, LogsSearchBarStore } from '../../../services/logs-search-bar.store';

const TRAFFIC_FILTER_GROUPBY_REQUEST_ID = 'TRAFFIC_FILTER_GROUPBY_REQUEST';

const defaultGroupbyParams = {
    orderby: '-count',
};

/**
 * Enum for the 3 types of options the dropdown can provide.
 * Correspond to the keys of TFilterObj.
 */
export enum DropdownOptionType {
    PROPERTY,
    OPERATOR,
    VALUE,
}

export interface ITrafficLogsSearchBarStates extends ILogsSearchBarStates {
    dropdownOptionType: DropdownOptionType;
}

const initialState: ITrafficLogsSearchBarStates = {
    inputFilter: {
        fullString: '',
        property: '',
        operator: '',
        groupbyValues: [],
    },
    dropdownOptionType: DropdownOptionType.PROPERTY,
    isLoading: false,
    isFilterBeingEdited: false,
    hasError: false,
};

/**
 * @description
 *      The TrafficLogsStore is the source of truth for filters.
 *      The search bar component reads filter state from the TrafficLogsStore
 *      and updates it according to search bar interaction.
 *
 *      This store manages state for any filter being typed or edited by the user
 *      and the dropdown.
 *
 * @author Yogesh Patil
 */
@Injectable()
export class TrafficLogsSearchBarStore extends LogsSearchBarStore<ITrafficLogsSearchBarStates> {
    // ********************************** Selectors **********************************

    /**
     * A pool ID to be included in the groupby request. Not part of the store's state.
     */
    private poolIdForRequest?: string;

    public readonly dropdownOptionType$ = this.select(state => state.dropdownOptionType);

    // ********************************** Effects **********************************

    /**
     * Add a filter to the central Traffic Logs store from the search bar.
     */
    public readonly addFilter = this.effect(
        LogsEffectsService.createSelectorTapEffect(
            this.inputFilterValue$,
            inputFilterValue => {
                // ignore if input is empty
                if (inputFilterValue) {
                    this.trafficLogsStore.addFilterQueryStrings(inputFilterValue);
                }

                this.resetSearchBar();
            },
        ),
    );

    /**
     * Edit an existing filter in the central Traffic Logs store.
     */
    public readonly editFilter = this.effect<number>(
        index$ => index$.pipe(
            withLatestFrom(this.inputFilterValue$),
            tap(([index, inputFilterValue]: [number, string]) => {
                // if new filter is empty, remove it altogether
                if (!inputFilterValue) {
                    this.trafficLogsStore.removeFilter(index);
                } else {
                    this.trafficLogsStore.editFilterQueryString(index, inputFilterValue);
                }

                this.setIsFilterBeingEdited(false);
                this.editIndex = null;
                this.resetSearchBar();
            }),
        ),
    );

    /**
     * Handle backspace event.
     * Remove last added filter if main search input is empty.
     * (Does not apply for editable filters).
     */
    public readonly handleBackspace = this.effect<void>(pipe(
        withLatestFrom(this.inputFilterValue$, this.trafficLogsStore.filters$),
        tap((valuesContainer: [void, string, string[]]) => {
            const inputFilterValue = valuesContainer[1];
            const filters = valuesContainer[2];

            if (filters.length !== 0 && !inputFilterValue) {
                this.trafficLogsStore.removeFilter(filters.length - 1);
            }
        }),
    ));

    /**
     * Effect to increment or decrement the dropdown stage.
     */
    public readonly incrementDropdownStage = this.effect(
        LogsEffectsService.createSelectorTapEffect(
            this.dropdownOptionType$,
            type => this.updateDropdownStage(type + 1),
        ),
    );

    public readonly decrementDropdownStage = this.effect(
        LogsEffectsService.createSelectorTapEffect(
            this.dropdownOptionType$,
            type => this.updateDropdownStage(type - 1),
        ),
    );

    /**
     * Make a groupby request based on current input filter property.
     */
    public readonly requestGroupbyValues: () => void;

    // ********************************** Updaters **********************************

    protected readonly setDropdownOptionType = this.updater(
        (state, dropdownOptionType: DropdownOptionType) => ({
            ...state,
            dropdownOptionType,
        }),
    );

    /**
     * Index of filter if filter is being edited.
     */
    protected editIndex: number;

    // ********************************** Constructor **********************************

    constructor(
        protected readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        protected readonly logsSearchBarDropdownService: LogsSearchBarDropdownService,
        logsEffectsService: LogsEffectsService,
    ) {
        super(initialState, trafficLogsStore, logsSearchBarDropdownService);

        this.initialState = initialState;

        // Initialize request effect
        const requestGroupbyValuesRequestEffect = this.effect<TLogsPageStateParams>(
            logsEffectsService.createLogsRequestEffect(
                TRAFFIC_FILTER_GROUPBY_REQUEST_ID,
                this.startLoading,
                this.handleApiData,
                this.handleApiError,
                this.handleApiComplete,
            ),
        );

        this.requestGroupbyValues = this.effect(
            groupbyParams$ => groupbyParams$.pipe(
                withLatestFrom(
                    this.trafficLogsStore.logsPageParams$,
                    this.inputFilterProperty$,
                    this.isFilterBeingEdited$,
                ),
                tap((valuesContainer: [void, TLogsPageStateParams, string, boolean]) => {
                    let groupbyParams = valuesContainer[1];
                    const inputFilterProperty = valuesContainer[2];
                    const isFilterBeingEdited = valuesContainer[3];

                    // If filter is being edited, ignore that filter when making
                    // the groupby request
                    if (isFilterBeingEdited) {
                        // Make new objects to avoid modifying state
                        groupbyParams = { ...groupbyParams };
                        groupbyParams.filters = [...groupbyParams.filters];
                        groupbyParams.filters.splice(this.editIndex, 1);
                    }

                    let fullParams: ILogsFullGroupbyParams = {
                        ...defaultGroupbyParams,
                        ...groupbyParams,
                        groupby: inputFilterProperty,
                    };

                    fullParams = addPoolIdFilterParams(fullParams, this.poolIdForRequest);

                    requestGroupbyValuesRequestEffect(fullParams);
                }),
            ),
        );
    }

    // ********************************** Methods **********************************

    /**
     * Set a pool ID to be used for subsequent groupby requests.
     */
    public setPoolIdForRequest(poolId: string): void {
        this.poolIdForRequest = poolId;
    }

    /**
     * Close the dropdown and reset the search bar state.
     */
    protected resetSearchBar(): void {
        this.logsSearchBarDropdownService.closeDropdown();
        this.setState(this.initialState);
    }

    /**
     * Update the dropdown stage.
     */
    protected updateDropdownStage(type: DropdownOptionType): void {
        this.setDropdownOptionType(type);
        this.logsSearchBarDropdownService.changeDropdownStage();
    }

    /**
     * Set params for filters that are being edited.
     */
    public setEdit(index: number): void {
        this.setIsFilterBeingEdited(true);
        this.editIndex = index;
    }
}
